// The "Alertas" feed — ported from mantis-business-front-brinks's src/routes/alertas.tsx.
//
// **B-91: one request, and only the alerts that are.** It used to load `filterOffices` for up to
// 500 closings — every column of the matrix, 7.6 s on dev, only to label each alert with its
// office and date — then `getCierreAlertsBatch` over them, and draw everything that came back:
// "Caja cuadrada $0" (good news), "Resuelta por los datos", attentions a redo had discarded, and
// the alert raised again after one was attended. 1.322 alerts on dev, 421 on one Bancamía day
// whose panel said one office was blocked. Now `listCierreAlerts` answers one client's alerts in
// the range asked for, with their offices, and only the open ones and those a person attended;
// `alertasDelFeed` applies the detail's own reading, closing by closing. A value that has not
// arrived ("blocked") is "media" and only a descuadre is "alta" (`prioridadEnFeed`), as the panel
// reads them since B-83. B-118: when the backend grades an alert by its amount (`priority`, from
// the client's `alert_priority:`), that priority is taken instead.
//
// **B-56: "Atendida" is real.** It used to be a `Set` of ids in this component — a sticker put on
// and taken off in one browser, lost on reload, so nobody could tell next morning which of 140
// offices had been reviewed. It now calls `resolveCierreAlert` with the operator's observation (the
// macro's column AK OBSERVACIONES) and the server writes who, when and what. Three consequences
// shape this file:
//
//   * **The feed no longer filters to OPEN.** An attended alert stays on screen with its state,
//     its author and its note, which is what the mural asks for — *"Esas alertas deben quedar
//     guardadas y tener el estado de revisadas o abiertas"* — and the only way the observation
//     someone just wrote survives a reload. `soloSinAtender`, decorative until now, became the real
//     filter, and every count asks `esAlertaAbierta` instead of a local `Set`.
//   * **There is no "Reabrir" and no "marcar todas".** Attending is permanent (`closing_alerts` is
//     append-only; a second attempt earns a 409), so a bulk button would write up to ~280
//     irreversible rows — 140 offices x caja and bóveda — signed by someone who read none of them,
//     which is the opposite of *"para cada caso pueda escribir un comentario"*.
//   * **Attending does not make the closing agree**, and the feed says so once: `lifecycle.js`
//     matches an incoming alert against the OPEN rows only, so a difference that still holds raises
//     a NEW alert on the next evaluation. Without that sentence the operator reads the reappearance
//     as their note having been lost.
//
// The feed reads by day: alerts are grouped by their closing's business date, newest first, each
// group collapsible, with a date filter above. Three decisions there are worth knowing:
//
//   * **It opens on "7 días", and every range is asked of the server** (B-91). Before, nothing was
//     pre-applied and the feed held the newest 500 closings, which on Bancamía is ~3 days: anything
//     older was gone without a word. Now a range brings all of its alerts, and one holding more than
//     the server sends says so.
//   * **The chips are anchored on the real Bogota day**, not on the newest date in the data the
//     way PanelCierres anchors its own. PanelCierres can afford a data-derived anchor because its
//     rows each print their date; here the day IS the heading, so a chip saying "Hoy" that
//     selected 18-08-2026 would be a lie, and a lie is worse than an empty result.
//   * **So emptiness is designed for, not avoided.** Every chip carries its own count — from the
//     server's `byDate`, so before the click — and a chip worth zero is disabled; when a range does
//     come back empty the empty state names the most recent alert and offers to jump to it.
//
// `orden` is still here, still only ever "prioridad": nothing in the JSX has ever been bound to
// it. Left alone deliberately — with the day groups supplying the outer order it now sorts
// *within* a day, which is coherent, and exposing it is a different change.
import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import {
  AlertTriangle,
  Bell,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronsDownUp,
  ChevronsUpDown,
  Download,
  Eye,
  Info,
  RotateCcw,
  Search,
  SearchX,
  Settings,
} from "lucide-react";
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  cn,
} from "@mantis-sas/ui";

import { selectAuthState } from "../../../store/selectors/authSelectors";
import { filterOffices, listCierreAlerts } from "../../../ApiCalls/mantisBusiness";
import {
  PRESETS_FECHA,
  agruparAlertasPorFecha,
  alertasDelFeed,
  ajusteDeAlerta,
  aplicarResolucion,
  atencionDeAlerta,
  conteosDeFechas,
  observacionDeAlerta,
  documentosDeAlerta,
  enRangoDeFechas,
  esAlertaAbierta,
  esFechaRelativa,
  etiquetaDeGrupoFecha,
  etiquetaDeRangoFecha,
  fechaDMY,
  prioridadEnFeed,
  rangoDePreset,
  resultadoDeAjuste,
  tonoDeAjuste,
} from "../../../utils/cierrePanel";
import { AVISO_ALERTAS } from "../../../utils/cierreManual";
import { getColombiaDateString, parseDateOnly } from "../../../utils/dateUtils";
import { FiltroRangoFechas } from "./FiltroColumna";
import { useAtenderAlerta, useEditarObservacion } from "./AtenderAlerta";
import { CierreDetailDialog } from "./CierreDetailDialog";
import { mensajeDeAlerta, nombreDeAlerta, nombreDeFuente } from "../../../utils/cierreNombres";
import { useNombresCierre } from "../../../hooks/useNombresCierre";
import { useAccesoCierres } from "../../../hooks/useAccesoCierres";
import { PERMISO, filasExportables, puede } from "../../../utils/cierrePermisos";
import { descargarAlertas, hojaDeAlertas } from "../../../utils/cierreAlertasExcel";
import { useClienteCierres } from "./ClienteCierresContext";
import { ConfiguracionAlertas } from "./ConfiguracionAlertas";
import "./styles/brinksTheme.css";
import { guia } from "../../../utils/cierreTours";

const TONO = {
  alta: { clase: "bg-brinks-danger-soft text-brinks-danger", icono: AlertTriangle, texto: "Alta", franja: "bg-brinks-danger", orden: 0 },
  media: { clase: "bg-brinks-warning-soft text-brinks-warning", icono: Bell, texto: "Media", franja: "bg-brinks-warning", orden: 1 },
  baja: { clase: "bg-brinks-surface text-brinks-muted-foreground", icono: Info, texto: "Baja", franja: "bg-muted-foreground/40", orden: 2 },
};
const PRIORIDADES = ["alta", "media", "baja"];
const RANGO_VACIO = { preset: "todo", desde: "", hasta: "" };
// B-91 — the range the feed opens on. Each range is asked of the server, so opening on every date
// the client has would load all of it on every visit.
const RANGO_INICIAL = { preset: "7", desde: "", hasta: "" };

function CierresAlertas(props) {
  // B-55 — which client the shell's selector is on. Every alert below is that client's.
  const { clientCode, listo } = useClienteCierres();
  // B-47 — readable names for the documents and the check each alert belongs to. Per client: the
  // labels come from that client's own `panel/names/<client>.yaml`.
  const nombres = useNombresCierre(props.token, props.mantisBusiness?._id, clientCode);
  // BR-38 — "Atender" is offered on the alerts of the offices this person may attend; to anyone
  // else the card is a reading, with "Ver cierre" still there.
  const acceso = useAccesoCierres();
  // B-92 — "Configuración de alertas": who gets the client's alert emails. The lists are the
  // client's, so changing them needs `cierres.notifications.manage` on the whole client.
  const puedeConfigurarAlertas = puede(acceso, PERMISO.CONFIGURAR_ALERTAS, { cliente: clientCode, entero: true });
  // B-93 — the feed as an Excel, for the range and filters on screen.
  const puedeExportar = puede(acceso, PERMISO.EXPORTAR, { cliente: clientCode });
  const [configAbierta, setConfigAbierta] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [alertas, setAlertas] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [prioridad, setPrioridad] = useState("todas");
  const [oficina, setOficina] = useState("todas");
  const [orden, setOrden] = useState("prioridad");
  const [soloSinAtender, setSoloSinAtender] = useState(false);
  // B-56 — said once, after the first alert of this visit is attended, rather than on every card:
  // the rule fires again on the next evaluation if the difference still holds.
  const [avisoReevaluacion, setAvisoReevaluacion] = useState(false);
  const [detalleClosingId, setDetalleClosingId] = useState(null);
  // B-80 — bumped after a closing is redone from its detail, so the screen reads it again.
  const [recarga, setRecarga] = useState(0);
  const [resultadoAjuste, setResultadoAjuste] = useState("");
  // The date filter, intent included. One object with an explicit `preset` rather than two loose
  // date strings, because "which chip is lit" then stays a `===` on a string instead of
  // reverse-engineering it from the dates the way PanelCierres does.
  const [rangoFecha, setRangoFecha] = useState(RANGO_INICIAL);
  // The keys of the groups the user has COLLAPSED — never the open ones. An empty set means
  // everything is open, which is the default we want and also what makes a group that appears
  // later (after the fetch resolves, or after a filter reveals a new day) start open with no
  // effect syncing state to data. A day someone closed also stays closed across filter changes.
  const [colapsados, setColapsados] = useState(() => new Set());
  // B-91 — what the server said about every date of the client (`byDate`) and about the range asked
  // for (`total`, `truncated`): the chips count themselves from it, and a shortened answer says so.
  const [porFecha, setPorFecha] = useState([]);
  const [totalRango, setTotalRango] = useState(0);
  const [truncado, setTruncado] = useState(false);
  // The panel row of the closing "Ver cierre" opened — asked for that one closing on the click,
  // so the detail has its extracted values without the feed loading every closing's row.
  const [filaDetalle, setFilaDetalle] = useState(null);

  // One wall-clock read per mount. A tab left open across midnight keeps yesterday's anchor until
  // it remounts, which is cheaper and far less surprising than a timer that silently relabels the
  // page under whoever is reading it.
  const hoy = useMemo(() => getColombiaDateString(), []);

  const rangoEfectivo = useMemo(
    () => rangoDePreset(rangoFecha.preset, hoy, rangoFecha),
    [rangoFecha, hoy]
  );

  useEffect(() => {
    // On first render, before the auth store finishes verifying the session, `mantisBusiness._id`
    // can briefly be undefined; a request then would come back empty and read as "no alerts".
    if (!props.mantisBusiness?._id) return undefined;
    // B-55 — and wait for the client list: a fetch that goes out without a `clientCode` is the
    // 400 a business with two cierre clients answers with.
    if (!listo) return undefined;

    let activo = true;
    (async () => {
      setCargando(true);
      // B-91 — one request: the client's alerts in the range, each with its closing's date, office
      // and region. It replaces `filterOffices` over 500 closings (7.6 s on dev, for the labels
      // alone) followed by `getCierreAlertsBatch`, and the 500-closing cap that silently dropped
      // everything older than ~3 days of Bancamía.
      const respuesta = await listCierreAlerts(props.token, props.mantisBusiness._id, clientCode, {
        since: rangoEfectivo.desde,
        until: rangoEfectivo.hasta,
      }).catch(() => null);
      if (!activo) return;
      if (!respuesta?.success) {
        setCargando(false);
        return;
      }

      const filas = (respuesta.data?.alerts || []).map((a) => ({
        id: a.id,
        closingId: a.closingId,
        titulo: a.message,
        message: a.message,
        severity: a.severity,
        discarded: a.discarded,
        oficina: a.officeName || a.officeCode || "",
        // BR-38 — where attending it would land: a permission is asked of the closing's office,
        // and one granted by region reaches it only through the office's own region.
        oficinaCodigo: a.officeCode || null,
        region: a.region || null,
        // `fecha` is the display string; `fechaISO` the comparable day every grouping runs on.
        fecha: fechaDMY(a.businessDate),
        fechaISO: parseDateOnly(a.businessDate),
        // B-118 — graded from the amount when the client says how (Bancamía); a backend that does
        // not send it, or an alert with no amount, keeps the severity's reading.
        prioridad: a.priority || prioridadEnFeed(a.severity),
        // B-118 — the amount the priority came from, for the Excel's "Monto del descuadre".
        monto: a.amount ?? null,
        ruleKey: a.ruleKey,
        documentos: documentosDeAlerta(a),
        // B-56 — the resolution as the table holds it. `esAlertaAbierta` reads `status`, and
        // the card prints the other three.
        status: a.status,
        resolvedBy: a.resolvedBy,
        resolvedAt: a.resolvedAt,
        resolutionNote: a.resolutionNote,
        // B-115 — what attending it may adjust, and what it did adjust.
        adjustable: a.adjustable || null,
        adjustment: a.adjustment || null,
        // B-116 — resolved by a person's correction: who, when and why.
        correctedBy: a.correctedBy || null,
        correctedAt: a.correctedAt || null,
        correctionReason: a.correctionReason || null,
      }));
      setAlertas(alertasDelFeed(filas));
      setPorFecha(respuesta.data?.byDate || []);
      setTotalRango(respuesta.data?.total || 0);
      setTruncado(!!respuesta.data?.truncated);
      setCargando(false);
    })();
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.token, props.mantisBusiness._id, clientCode, listo, recarga, rangoEfectivo.desde, rangoEfectivo.hasta]);

  // B-91 — "Ver cierre": the closing's panel row, asked for that one date and office, so the
  // detail shows its extracted values and offers "Rehacer" as it does from the panel.
  useEffect(() => {
    setFilaDetalle(null);
    if (!detalleClosingId) return undefined;
    const alerta = alertas.find((a) => a.closingId === detalleClosingId);
    if (!alerta?.fechaISO) return undefined;
    let activo = true;
    (async () => {
      const filtros = { startDate: alerta.fechaISO, endDate: alerta.fechaISO, office: alerta.oficinaCodigo || undefined };
      const r = await filterOffices(props.token, filtros, 100, props.mantisBusiness._id, "brinks", clientCode).catch(() => null);
      if (!activo || !r?.success) return;
      setFilaDetalle((r.data || []).find((row) => row.closing_id === detalleClosingId) || null);
    })();
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detalleClosingId]);

  const oficinasUnicas = useMemo(
    () => [...new Set(alertas.map((a) => a.oficina))].sort((a, b) => a.localeCompare(b)),
    [alertas]
  );

  // Open ones only, ignoring every filter but the date range (B-91: the range is what was asked of
  // the server, so it is what is loaded). These three tiles are also the priority filter control (`aria-pressed`, click sets
  // `prioridad`), and a button reading 0 because of an unrelated date filter looks broken rather
  // than informative. B-56 — "open" is now `closing_alerts.status`, not a local Set, so the number
  // survives a reload and means the same thing to two people looking at it.
  const conteo = useMemo(
    () =>
      PRIORIDADES.reduce(
        (acc, p) => ({
          ...acc,
          [p]: alertas.filter((a) => a.prioridad === p && esAlertaAbierta(a)).length,
        }),
        {}
      ),
    [alertas]
  );

  // Asked of the resolved range, not of the preset, so a "personalizado" whose two inputs were
  // cleared does not count as a bounded range.
  const filtroFechaActivo = !!(rangoEfectivo.desde || rangoEfectivo.hasta);
  // B-91 — the feed opens on "7 días"; only a range other than that is a filter "Restablecer" undoes.
  const fechaCambiada = rangoFecha.preset !== RANGO_INICIAL.preset;

  const hayFiltros =
    busqueda.trim() !== "" || prioridad !== "todas" || oficina !== "todas" || soloSinAtender || fechaCambiada;

  // Everything except the date range, so the chips below can each count themselves against the
  // other filters. Without the split every chip would report the same number.
  const filtradasSinFecha = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    const filtradas = alertas.filter((a) => {
      if (prioridad !== "todas" && a.prioridad !== prioridad) return false;
      if (oficina !== "todas" && a.oficina !== oficina) return false;
      if (soloSinAtender && !esAlertaAbierta(a)) return false;
      // Every form of every cited document — the key a person copies from elsewhere, the readable
      // name on screen, and the file's own name. Searching for any of the three finds the alert.
      const buscable = [
        a.titulo,
        a.oficina,
        ...a.documentos.map((d) => d.sourceKey),
        ...a.documentos.map((d) => nombreDeFuente(nombres, d.sourceKey)),
        nombreDeAlerta(nombres, a.ruleKey),
        ...a.documentos.map((d) => d.archivo).filter(Boolean),
      ].join(" ");
      if (q && !buscable.toLowerCase().includes(q)) return false;
      return true;
    });
    const porPrioridad = (a, b) => TONO[a.prioridad].orden - TONO[b.prioridad].orden;
    return [...filtradas].sort((a, b) => {
      if (orden === "prioridad") return porPrioridad(a, b);
      if (orden === "oficina") return a.oficina.localeCompare(b.oficina) || porPrioridad(a, b);
      return porPrioridad(a, b);
    });
  }, [alertas, busqueda, prioridad, oficina, soloSinAtender, orden, nombres]);

  const visibles = useMemo(
    () => filtradasSinFecha.filter((a) => enRangoDeFechas(a.fechaISO, rangoEfectivo)),
    [filtradasSinFecha, rangoEfectivo]
  );

  // The day groups supply the outer ordering; the priority sort above survives untouched as the
  // order *within* a day, because the helper preserves the order it is given.
  const grupos = useMemo(() => agruparAlertasPorFecha(visibles), [visibles]);

  // What each chip would bring — so a chip that would empty the page says so before it is clicked.
  // B-91: each chip asks the server for its range, so it is counted from the server's `byDate`
  // (every date of the client), not from the alerts on screen.
  const conteosPreset = useMemo(() => conteosDeFechas(porFecha, PRESETS_FECHA, hoy), [porFecha, hoy]);

  // `byDate` is newest first: the most recent date with an alert, and how many sit outside the range.
  const fechaMasReciente = porFecha[0]?.date || null;
  const fueraDelRango = Math.max(0, porFecha.reduce((n, d) => n + (d.count || 0), 0) - totalRango);

  const restablecer = () => {
    setBusqueda("");
    setPrioridad("todas");
    setOficina("todas");
    setOrden("prioridad");
    setSoloSinAtender(false);
    setRangoFecha(RANGO_INICIAL);
  };

  // B-56 — the server's answer, written into the one card it belongs to. Not a refetch: the row is
  // all that changed, and the card shows what the server stored.
  //
  // B-115 — attended with an adjustment, the following days were evaluated again and their repeated
  // descuadre may be gone: then the feed is asked again, and what happened is said.
  const alertaAtendida = (resuelta, data) => {
    if (!resuelta) return;
    // With an adjustment the "it will be raised again" warning is not true of the next day: say
    // what the adjustment did instead.
    if (data?.adjustment) {
      setResultadoAjuste(resultadoDeAjuste(data));
      setRecarga((n) => n + 1);
      return;
    }
    setAvisoReevaluacion(true);
    setAlertas((prev) => aplicarResolucion(prev, resuelta));
  };

  // The changed observation, written into its card the same way — and with no re-evaluation notice:
  // a note moves no figure.
  const observacionEditada = (editada) => {
    if (!editada) return;
    setAlertas((prev) => aplicarResolucion(prev, editada));
  };

  const alternarGrupo = (clave) =>
    setColapsados((prev) => {
      const next = new Set(prev);
      if (next.has(clave)) next.delete(clave);
      else next.add(clave);
      return next;
    });

  // B-93 — what is on screen, in its order (day, then the order within it), keeping only the
  // offices `cierres.export` reaches. A shortened answer is refused rather than written short.
  const exportar = () => {
    const filas = filasExportables(acceso, clientCode, grupos.flatMap((g) => g.alertas), (a) => ({
      oficina: a.oficinaCodigo,
      region: a.region,
    }));
    descargarAlertas(hojaDeAlertas({ alertas: filas, nombres, rango: rangoEfectivo, clientCode })).catch((e) => {
      // The click is the only feedback surface; a failed download is logged, not swallowed.
      console.error("No se pudo generar el Excel", e);
    });
  };

  const todoColapsado = grupos.length > 0 && grupos.every((g) => colapsados.has(g.clave));
  const alternarTodos = () =>
    setColapsados(todoColapsado ? new Set() : new Set(grupos.map((g) => g.clave)));

  return (
    <div className="brinks-scope font-redhat min-w-0 flex-1 bg-background p-3 sm:p-4">
      <header className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="font-lora text-[21px] font-semibold text-brinks-foreground">Alertas</h1>
          <p className="mt-0.5 text-[13px] text-brinks-muted-foreground">
            Diferencias por revisar, y las que ya quedaron atendidas
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {puedeExportar && listo ? (
            <Button
              variant="outline"
              className="h-9 gap-1.5 text-[12.5px]"
              onClick={exportar}
              disabled={cargando || visibles.length === 0 || truncado}
              title={truncado ? "Elige un rango más corto para exportar todas las alertas" : undefined}
            >
              <Download className="h-4 w-4" /> Exportar Excel
            </Button>
          ) : null}
          {puedeConfigurarAlertas && listo ? (
            <Button variant="outline" className="h-9 gap-1.5 text-[12.5px]" {...guia("Configuración de alertas", "Quién recibe el resumen diario y el aviso inmediato, y desde qué monto una diferencia es grave.")} onClick={() => setConfigAbierta(true)}>
              <Settings className="h-4 w-4" /> Configuración de alertas
            </Button>
          ) : null}
        </div>
      </header>

      {/* B-56 — once per visit, after the first alert is attended. Attending records a judgement;
          it does not make the closing agree, and the rule raises a fresh alert on the next
          evaluation while the difference holds. */}
      {avisoReevaluacion ? (
        <div role="status" className="mb-3 rounded-[10px] border border-brinks-warning-border bg-brinks-warning-soft px-3 py-2 text-[12.5px] text-brinks-warning">
          {AVISO_ALERTAS}
        </div>
      ) : null}
      {resultadoAjuste ? (
        <div role="status" className="mb-3 rounded-[10px] border border-brinks-border bg-brinks-surface px-3 py-2 text-[12.5px] text-brinks-foreground">
          {resultadoAjuste}
        </div>
      ) : null}

      <div {...guia("Prioridad", "Alertas de prioridad alta, media y baja. Toca una para ver solo esas.")} className="mb-3 grid grid-cols-3 gap-2 sm:gap-3">
        {PRIORIDADES.map((p) => {
          const t = TONO[p];
          const activo = prioridad === p;
          return (
            <button
              key={p}
              type="button"
              aria-pressed={activo}
              onClick={() => setPrioridad(activo ? "todas" : p)}
              className={cn(
                "flex items-center gap-2.5 rounded-[12px] border bg-card px-3 py-2.5 text-left shadow-brinks-card transition-colors duration-200 sm:gap-3 sm:px-4 sm:py-3",
                activo ? "border-foreground/30" : "hover:bg-brinks-surface-hover"
              )}
            >
              <span className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-[10px]", t.clase)}>
                <t.icono className="h-4.5 w-4.5" />
              </span>
              <span className="min-w-0">
                <span className="block text-lg font-semibold leading-tight tabular-nums sm:text-xl">
                  {conteo[p] || 0}
                </span>
                <span className="block truncate text-[11px] text-brinks-muted-foreground sm:text-xs">
                  {t.texto} prioridad
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <div {...guia("Filtros", "Busca por título u oficina y filtra por fecha, región o estado.")} className="mb-3 grid grid-cols-1 gap-2 rounded-[12px] border bg-card p-3 shadow-brinks-card sm:grid-cols-2 lg:grid-cols-[minmax(180px,1.2fr)_repeat(3,minmax(0,1fr))_auto_auto] lg:items-center">
        <span className="relative block min-w-0">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brinks-muted-foreground" />
          <input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por título u oficina..."
            className="h-10 w-full min-w-0 rounded-[10px] border bg-card pl-9 pr-3 text-[13px] outline-none transition placeholder:text-brinks-muted-foreground/70 focus:border-brinks-border-strong"
          />
        </span>

        <FiltroFecha valor={rangoFecha} onChange={setRangoFecha} conteos={conteosPreset} activo={filtroFechaActivo} />

        <Select value={prioridad} onValueChange={setPrioridad}>
          <SelectTrigger className="h-10 w-full rounded-[10px] text-[13px]" aria-label="Filtrar por prioridad">
            <SelectValue>{prioridad === "todas" ? "Todas las prioridades" : TONO[prioridad].texto}</SelectValue>
          </SelectTrigger>
          <SelectContent className="brinks-scope font-redhat">
            <SelectItem value="todas">Todas las prioridades</SelectItem>
            <SelectItem value="alta">Alta</SelectItem>
            <SelectItem value="media">Media</SelectItem>
            <SelectItem value="baja">Baja</SelectItem>
          </SelectContent>
        </Select>

        <Select value={oficina} onValueChange={setOficina}>
          <SelectTrigger className="h-10 w-full rounded-[10px] text-[13px]" aria-label="Filtrar por oficina">
            <SelectValue>{oficina === "todas" ? "Todas las oficinas" : oficina}</SelectValue>
          </SelectTrigger>
          <SelectContent className="brinks-scope font-redhat">
            <SelectItem value="todas">Todas las oficinas</SelectItem>
            {oficinasUnicas.map((o) => (
              <SelectItem key={o} value={o}>
                {o}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <button
          type="button"
          aria-pressed={soloSinAtender}
          onClick={() => setSoloSinAtender((v) => !v)}
          className={cn(
            "h-10 w-full rounded-[10px] border px-3 text-[13px] font-medium transition lg:w-auto",
            soloSinAtender ? "border-transparent bg-primary text-primary-foreground" : "bg-card text-brinks-muted-foreground hover:bg-brinks-surface-hover"
          )}
        >
          Solo sin atender
        </button>

        {hayFiltros ? (
          <Button variant="outline" size="sm" className="h-10" onClick={restablecer}>
            <RotateCcw className="h-3.5 w-3.5" />
            Restablecer
          </Button>
        ) : (
          <span className="hidden lg:block" />
        )}
      </div>

      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-brinks-muted-foreground">
          Mostrando <span className="font-semibold text-brinks-foreground">{visibles.length}</span> de {alertas.length}{" "}
          alertas
          {grupos.length > 0 ? ` en ${grupos.length} ${grupos.length === 1 ? "fecha" : "fechas"}` : ""}
        </p>
        {/* B-91 — the server answers at most a few thousand; a range holding more says so rather
            than showing a part as if it were all. */}
        {truncado ? (
          <p role="status" className="w-full text-xs text-brinks-warning">
            Se muestran {alertas.length} de {totalRango} alertas de este rango. Elige un rango más corto para verlas todas.
          </p>
        ) : null}
        {grupos.length > 1 ? (
          <Button variant="ghost" size="sm" className="h-8 gap-1 px-2 text-[12px]" onClick={alternarTodos}>
            {todoColapsado ? <ChevronsUpDown className="h-3.5 w-3.5" /> : <ChevronsDownUp className="h-3.5 w-3.5" />}
            {todoColapsado ? "Expandir todo" : "Colapsar todo"}
          </Button>
        ) : null}
      </div>

      {cargando ? (
        <div className="grid place-items-center rounded-[12px] border bg-card px-6 py-14 text-center shadow-brinks-card">
          <Spinner size="lg" />
        </div>
      ) : visibles.length === 0 ? (
        <div className="grid place-items-center rounded-[12px] border bg-card px-6 py-14 text-center shadow-brinks-card">
          <span className="mb-3 grid h-11 w-11 place-items-center rounded-full bg-brinks-surface text-brinks-muted-foreground">
            <SearchX className="h-5 w-5" />
          </span>
          {/* B-56 — attended ones count too, so "no alerts" means none on any closing, not merely
              none open. B-91: `alertas` is only the range asked for; `porFecha` is every date. */}
          <p className="text-sm font-semibold">
            {porFecha.length === 0 ? "No hay alertas" : "No hay alertas con estos filtros"}
          </p>
          <p className="mt-1 text-[13px] text-brinks-muted-foreground">
            {porFecha.length === 0
              ? "Ningún cierre tiene alertas, ni abiertas ni atendidas."
              : "Ajusta la búsqueda o restablece los filtros para ver todo."}
          </p>
          {/* A range that matches nothing is a dead end unless the page says where the data
              actually is. Gated on the date filter so a zero caused by the search box does not get
              a misleading date explanation. */}
          {alertas.length === 0 && filtroFechaActivo && fechaMasReciente ? (
            <p className="mt-1 text-[13px] text-brinks-muted-foreground">
              La alerta más reciente es del{" "}
              <span className="font-semibold text-brinks-foreground tabular-nums">{fechaDMY(fechaMasReciente)}</span>, y
              hay {fueraDelRango} fuera de este rango.
            </p>
          ) : null}
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            {alertas.length === 0 && filtroFechaActivo && fechaMasReciente ? (
              <Button
                size="sm"
                onClick={() => setRangoFecha({ preset: "personalizado", desde: fechaMasReciente, hasta: fechaMasReciente })}
              >
                <CalendarDays className="h-3.5 w-3.5" />
                Ver esa fecha
              </Button>
            ) : null}
            {hayFiltros ? (
              <Button variant="outline" size="sm" onClick={restablecer}>
                <RotateCcw className="h-3.5 w-3.5" />
                Limpiar filtros
              </Button>
            ) : null}
          </div>
        </div>
      ) : (
        <div {...guia("Alertas por día", "Abre el cierre de una alerta o márcala como atendida con una observación. Una alerta atendida no se puede reabrir.")} className="space-y-3">
          {grupos.map((g) => {
            const colapsado = colapsados.has(g.clave);
            const idPanel = `alertas-grupo-${g.clave || "sin-fecha"}`;
            const sinAtender = g.alertas.filter(esAlertaAbierta).length;
            return (
              <section key={g.clave || "sin-fecha"}>
                <button
                  type="button"
                  onClick={() => alternarGrupo(g.clave)}
                  aria-expanded={!colapsado}
                  aria-controls={idPanel}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-[10px] bg-brinks-surface px-3 py-2 text-left transition-colors duration-200 hover:bg-brinks-surface-hover",
                    sinAtender === 0 && "opacity-70"
                  )}
                >
                  <ChevronDown
                    aria-hidden
                    className={cn(
                      "h-4 w-4 shrink-0 text-brinks-muted-foreground transition-transform duration-200",
                      colapsado && "-rotate-90"
                    )}
                  />
                  <span className="truncate text-[13px] font-semibold text-brinks-foreground">
                    {etiquetaDeGrupoFecha(g.clave, hoy)}
                  </span>
                  {/* "Hoy"/"Ayer" never travel without the real date beside them — a relative
                      heading is a convenience, never something the reader has to take on trust. */}
                  {esFechaRelativa(g.clave, hoy) ? (
                    <span className="shrink-0 text-[11px] text-brinks-muted-foreground tabular-nums">
                      · {fechaDMY(g.clave)}
                    </span>
                  ) : null}
                  <span
                    className="shrink-0 rounded-full bg-card px-2 py-0.5 text-[11px] font-semibold tabular-nums"
                    aria-label={`${g.alertas.length} ${g.alertas.length === 1 ? "alerta" : "alertas"}`}
                  >
                    {g.alertas.length}
                  </span>
                  {/* What a folded day still says about itself: the only reason to open one is
                      "does it hold an alta". Dots rather than full badges, so they do not compete
                      with the cards' own priority badges one level down. */}
                  <span className="ml-auto hidden shrink-0 items-center gap-2.5 sm:flex">
                    {PRIORIDADES.map((p) => {
                      const n = g.alertas.filter((a) => a.prioridad === p).length;
                      if (n === 0) return null;
                      return (
                        <span
                          key={p}
                          className="flex items-center gap-1 text-[11px] text-brinks-muted-foreground"
                          aria-label={`${n} de prioridad ${TONO[p].texto}`}
                        >
                          <span aria-hidden className={cn("h-1.5 w-1.5 rounded-full", TONO[p].franja)} />
                          <span className="tabular-nums">{n}</span>
                        </span>
                      );
                    })}
                    {sinAtender !== g.alertas.length ? (
                      <span className="text-[11px] text-brinks-muted-foreground tabular-nums">
                        {sinAtender} sin atender
                      </span>
                    ) : null}
                  </span>
                </button>

                {colapsado ? null : (
                  <ul id={idPanel} className="mt-2.5 space-y-2.5">
                    {g.alertas.map((a) => (
                      <TarjetaAlerta
                        nombres={nombres}
                        key={a.id}
                        alerta={a}
                        token={props.token}
                        mantisBusinessId={props.mantisBusiness._id}
                        onVerCierre={setDetalleClosingId}
                        onAtendida={alertaAtendida}
                        onEditada={observacionEditada}
                        puedeAtender={puede(acceso, PERMISO.ATENDER_ALERTAS, {
                          cliente: clientCode,
                          oficina: a.oficinaCodigo,
                          region: a.region,
                        })}
                      />
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      )}

      <CierreDetailDialog
        closingId={detalleClosingId}
        panelRow={filaDetalle}
        token={props.token}
        mantisBusinessId={props.mantisBusiness._id}
        onOpenChange={(open) => !open && setDetalleClosingId(null)}
        onCambio={() => setRecarga((n) => n + 1)}
      />

      {configAbierta ? (
        <ConfiguracionAlertas
          open={configAbierta}
          token={props.token}
          mantisBusinessId={props.mantisBusiness._id}
          clientCode={clientCode}
          onClose={() => setConfigAbierta(false)}
        />
      ) : null}
    </div>
  );
}

// The date filter: four chips plus the typed range, in a popover shaped like the Selects beside
// it. `FiltroColumna`'s own shell is not reused — its trigger is a 24px funnel built for a table
// header — but `FiltroRangoFechas` is, with `atajos` omitted so its chip row renders nothing and
// only its two brinks-styled `<input type="date">` come through. Those inputs are the whole reason
// `PopoverContent` must carry `brinks-scope`: Radix portals it to `document.body`, outside the
// page root, where the unscoped rule in `businessBackofficeStyles.scss` paints every date input's
// digits white and the fix in `brinksTheme.css` (scoped to `.brinks-scope`) never reaches it.
function FiltroFecha({ valor, onChange, conteos, activo }) {
  const etiqueta = etiquetaDeRangoFecha(valor.preset, valor);
  const aplicarPreset = (clave) => onChange({ preset: clave, desde: "", hasta: "" });
  // Both inputs emptied is "todo" again, not an active "personalizado" — otherwise "Restablecer"
  // would sit there offering to clear a filter that is not filtering anything.
  const aplicarRango = (desde, hasta) =>
    onChange(desde || hasta ? { preset: "personalizado", desde, hasta } : RANGO_VACIO);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Filtrar por fecha"
          title={etiqueta}
          className={cn(
            "flex h-10 w-full min-w-0 items-center gap-2 rounded-[10px] border bg-card px-3 text-left text-[13px] outline-none transition-colors duration-200 hover:bg-brinks-surface-hover focus:border-brinks-border-strong",
            activo && "border-brinks-border-strong"
          )}
        >
          <CalendarDays className="h-4 w-4 shrink-0 text-brinks-muted-foreground" />
          <span className="truncate">{etiqueta}</span>
          {activo ? <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" /> : null}
          <ChevronDown aria-hidden className="ml-auto h-3.5 w-3.5 shrink-0 text-brinks-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="brinks-scope font-redhat w-[280px] rounded-[12px] border bg-popover p-3 shadow-lg duration-200"
      >
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.04em] text-brinks-muted-foreground">
          Filtrar por fecha
        </p>
        <div className="mb-3 flex flex-wrap gap-1.5">
          {PRESETS_FECHA.map((p) => {
            const n = conteos[p.clave] ?? 0;
            const seleccionado = valor.preset === p.clave;
            return (
              <button
                key={p.clave}
                type="button"
                aria-pressed={seleccionado}
                // A chip worth nothing is disabled rather than silently emptying the page. This is
                // the point at which "never hide real data" becomes visible to the reader.
                disabled={n === 0 && !seleccionado}
                onClick={() => aplicarPreset(p.clave)}
                className={cn(
                  "flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] font-medium transition-colors duration-200",
                  seleccionado
                    ? "border-primary bg-primary text-primary-foreground"
                    : "bg-card text-brinks-muted-foreground hover:text-brinks-foreground",
                  n === 0 && !seleccionado && "cursor-not-allowed opacity-50 hover:text-brinks-muted-foreground"
                )}
              >
                {p.etiqueta}
                <span className="tabular-nums opacity-70">{n}</span>
              </button>
            );
          })}
        </div>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.04em] text-brinks-muted-foreground">
          Personalizado
        </p>
        <FiltroRangoFechas desde={valor.desde} hasta={valor.hasta} onChange={aplicarRango} />
      </PopoverContent>
    </Popover>
  );
}

// One alert. Lifted out of the list only because the cards now sit two levels deep inside their
// day group and inlining 50 lines of JSX there made the render unreadable.
//
// The grid is `sm:items-start`, not `items-center`: the card grew two lines in B-45 and centring
// left the icon and the buttons floating against a taller block.
//
// B-56 — two states, and `atendida` is read off the row's own `status` rather than passed in from a
// local Set. An attended card keeps the dimming and the strikethrough it always had, drops its
// action, and gains the line that says who attended it and what they wrote: that observation is the
// macro's column AK, and a card that showed the alert without it would be hiding the only part a
// person authored.
function TarjetaAlerta({ alerta, token, mantisBusinessId, onVerCierre, onAtendida, onEditada, nombres, puedeAtender = false }) {
  const t = TONO[alerta.prioridad];
  const atendida = !esAlertaAbierta(alerta);
  const atencion = atencionDeAlerta(alerta);
  // Called unconditionally — a hook cannot sit behind the `atendida` branch below, and an attended
  // alert simply never renders either half of it.
  const atender = useAtenderAlerta({
    alertaId: alerta.id,
    closingId: alerta.closingId,
    token,
    mantisBusinessId,
    onAtendida,
    ajustable: alerta.adjustable,
  });
  // Only what a PERSON attended has an observation to change; one the data resolved carries the
  // engine's sentence. Same permission as attending.
  const editar = useEditarObservacion({
    alerta,
    closingId: alerta.closingId,
    token,
    mantisBusinessId,
    onEditada,
  });
  const puedeEditar = puedeAtender && alerta.status === "RESOLVED_BY_USER" && !!onEditada && !editar.abierto;
  return (
    <li
      className={cn(
        "relative overflow-hidden rounded-[12px] border bg-card shadow-brinks-card transition-opacity duration-200",
        atendida && "opacity-55"
      )}
    >
      <span aria-hidden className={cn("absolute inset-y-0 left-0 w-[3px]", t.franja)} />
      <div className="flex flex-col gap-3 p-3 pl-4 sm:grid sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-start sm:gap-3.5">
        <span className={cn("grid h-9 w-10 shrink-0 place-items-center rounded-xl", t.clase)}>
          <t.icono className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <p className={cn("truncate font-semibold", atendida && "line-through decoration-foreground/40")}>
            {mensajeDeAlerta(nombres, alerta.titulo)}
          </p>
          <p className="mt-1 truncate text-xs text-brinks-muted-foreground">
            {nombreDeAlerta(nombres, alerta.ruleKey)} · {alerta.oficina} · {alerta.fecha}
          </p>
          {alerta.documentos.length > 0 ? (
            <>
              <p className="mt-1.5 truncate text-[12px] font-medium text-brinks-foreground">
                Archivos: {alerta.documentos.map((d) => nombreDeFuente(nombres, d.sourceKey)).join(", ")}
              </p>
              {/* Only the files that exist. A cited document nobody uploaded has no name
                  to print, and printing its key here would just repeat the line above. */}
              {alerta.documentos.some((d) => d.archivo) ? (
                <p className="truncate text-[10.5px] text-brinks-muted-foreground">
                  {alerta.documentos.filter((d) => d.archivo).map((d) => d.archivo).join(", ")}
                </p>
              ) : null}
            </>
          ) : null}
          {/* B-56 — the observation is NOT truncated. It is a sentence a person wrote for the next
              person to read, and the next person needs the end of it. */}
          {atendida && atencion ? (
            <p className="mt-1.5 flex items-start gap-1.5 text-[12px] text-brinks-muted-foreground">
              <Check aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brinks-success" />
              <span>
                <span className="font-medium text-brinks-foreground">{atencion}</span>
                {observacionDeAlerta(alerta) ? <span className="block">«{observacionDeAlerta(alerta)}»</span> : null}
                {ajusteDeAlerta(alerta) ? (
                  <span className={cn("block font-medium", tonoDeAjuste(alerta.adjustment?.amount))}>{ajusteDeAlerta(alerta)}</span>
                ) : null}
              </span>
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap sm:justify-end">
          <span className={cn("shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold", t.clase)}>{t.texto}</span>
          <Button size="sm" className="h-9" onClick={() => onVerCierre(alerta.closingId)}>
            <Eye className="h-4 w-4" />
            Ver cierre
          </Button>
          {/* No "Reabrir" for an attended one: `closing_alerts` is append-only and the server
              refuses a second write with a 409. Its observation can still be changed. */}
          {atendida || !puedeAtender ? null : atender.boton}
          {puedeEditar ? editar.boton : null}
        </div>
        {/* Full width, under the three columns — a textarea inside the `auto` actions column would
            widen it and squeeze the message. */}
        {atender.formulario ? <div className="sm:col-span-3">{atender.formulario}</div> : null}
        {editar.formulario ? <div className="sm:col-span-3">{editar.formulario}</div> : null}
      </div>
    </li>
  );
}

export default connect(selectAuthState)(CierresAlertas);
